import { useToast } from "@/app/hooks/toast/useToast";
import { useTranslations } from "next-intl";
import { useTenantBranding } from "@/app/context/TenantBrandingContext";
import { useSupportEmail } from "@/app/hooks/useSupportEmail";

export const TermsAndConditionsCommon = () => {
  const { showToast } = useToast();
  const t = useTranslations();
  const { name: tenantName } = useTenantBranding();
  const supportEmail = useSupportEmail();

  const handleEmailCopy = () => {
    navigator.clipboard.writeText(supportEmail);
    showToast(t("Other.emailAddressCopied"), "success");
  };

  const handleGateUserAgreementCopy = () => {
    navigator.clipboard.writeText("https://www.gate.com/user-agreement");
    showToast(t("Other.linkCopied"), "success");
  };

  return (
    <>
      {/* Introduction */}
      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.GateTravelTermsOfUse")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          <strong>{t("TermsOfUse.lastRevised")}</strong>
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.AcceptanceOfGateTravelTerms")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t.rich("TermsOfUse.AcceptanceOfGateTravelTermsList.1", {
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t.rich("TermsOfUse.AcceptanceOfGateTravelTermsList.2", {
            strong: (chunks) => <strong>{chunks}</strong>,
            button: (chunks) => (
              <button
                type="button"
                onClick={handleGateUserAgreementCopy}
                aria-label="Copy User Agreement URL"
                className="text-sleap-purple-300 hover:underline inline bg-transparent border-0 p-0 m-0 cursor-pointer focus:outline-none text-base font-normal font-switzer leading-normal"
              >
                {chunks}
              </button>
            ),
          })}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.AcceptanceOfGateTravelTermsList.3")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.AcceptanceOfGateTravelTermsList.4")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.AcceptanceOfGateTravelTermsList.5")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t.rich("TermsOfUse.AcceptanceOfGateTravelTermsList.6", {
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t.rich("TermsOfUse.AcceptanceOfGateTravelTermsList.7", {
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
          <ul className="list-disc pl-6">
            <li>
              {t.rich("TermsOfUse.AcceptanceOfGateTravelTermsList.8", {
                strong: (chunks) => <strong>{chunks}</strong>,
              })}
            </li>
            <li>
              {t.rich("TermsOfUse.AcceptanceOfGateTravelTermsList.9", {
                strong: (chunks) => <strong>{chunks}</strong>,
              })}
            </li>
            <li>
              {t.rich("TermsOfUse.AcceptanceOfGateTravelTermsList.10", {
                strong: (chunks) => <strong>{chunks}</strong>,
              })}
            </li>
            <li>{t("TermsOfUse.AcceptanceOfGateTravelTermsList.11")}</li>
            <li>
              {t.rich("TermsOfUse.AcceptanceOfGateTravelTermsList.12", {
                strong: (chunks) => <strong>{chunks}</strong>,
              })}
            </li>
          </ul>
          {t.rich("TermsOfUse.AcceptanceOfGateTravelTermsList.13", {
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t.rich("TermsOfUse.AcceptanceOfGateTravelTermsList.14", {
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.eligibilityAndParticipationOfGateTravelService")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.eligibilityAndParticipationOfGateTravelServiceList.1")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.eligibilityAndParticipationOfGateTravelServiceList.2")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.eligibilityAndParticipationOfGateTravelServiceList.3")}
          <ul className="list-disc pl-6">
            <li>
              {t(
                "TermsOfUse.eligibilityAndParticipationOfGateTravelServiceList.4"
              )}
            </li>
            <li>
              {t(
                "TermsOfUse.eligibilityAndParticipationOfGateTravelServiceList.5"
              )}
            </li>
            <li>
              {t(
                "TermsOfUse.eligibilityAndParticipationOfGateTravelServiceList.6"
              )}
            </li>
            <li>
              {t(
                "TermsOfUse.eligibilityAndParticipationOfGateTravelServiceList.7"
              )}
            </li>
          </ul>
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.eligibilityAndParticipationOfGateTravelServiceList.8")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.eligibilityAndParticipationOfGateTravelServiceList.9")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t(
            "TermsOfUse.eligibilityAndParticipationOfGateTravelServiceList.10"
          )}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t(
            "TermsOfUse.eligibilityAndParticipationOfGateTravelServiceList.11"
          )}
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.featuresOfGateTravel")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t.rich("TermsOfUse.featuresOfGateTravelList.1", {
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.featuresOfGateTravelList.2")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.featuresOfGateTravelList.3")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.featuresOfGateTravelList.4")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.featuresOfGateTravelList.5")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.featuresOfGateTravelList.6")}
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.yourUseOfThePlatform")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.yourUseOfThePlatformList.1")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.yourUseOfThePlatformList.2")}
          <ul className="list-disc pl-6">
            <li>{t("TermsOfUse.yourUseOfThePlatformList.3")}</li>
            <li>{t("TermsOfUse.yourUseOfThePlatformList.4")}</li>
            <li>{t("TermsOfUse.yourUseOfThePlatformList.5")}</li>
            <li>{t("TermsOfUse.yourUseOfThePlatformList.6")}</li>
            <li>{t("TermsOfUse.yourUseOfThePlatformList.7")}</li>
            <li>{t("TermsOfUse.yourUseOfThePlatformList.8")}</li>
          </ul>
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.yourUseOfThePlatformList.9")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.yourUseOfThePlatformList.10")}
          <ul className="list-disc pl-6">
            <li>{t("TermsOfUse.yourUseOfThePlatformList.11")}</li>
            <li>{t("TermsOfUse.yourUseOfThePlatformList.12")}</li>
            <li>{t("TermsOfUse.yourUseOfThePlatformList.13")}</li>
            <li>{t("TermsOfUse.yourUseOfThePlatformList.14")}</li>
            <li>{t("TermsOfUse.yourUseOfThePlatformList.15")}</li>
            <li>{t("TermsOfUse.yourUseOfThePlatformList.16")}</li>
            <li>{t("TermsOfUse.yourUseOfThePlatformList.17")}</li>
            <li>{t("TermsOfUse.yourUseOfThePlatformList.18")}</li>
            <li>{t("TermsOfUse.yourUseOfThePlatformList.19")}</li>
            <li>{t("TermsOfUse.yourUseOfThePlatformList.20")}</li>
            <li>{t("TermsOfUse.yourUseOfThePlatformList.21")}</li>
            <li>{t("TermsOfUse.yourUseOfThePlatformList.22")}</li>
          </ul>
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.yourUseOfThePlatformList.23")}
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.platformContentAndAvailability")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.platformContentAndAvailabilityList.1")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.platformContentAndAvailabilityList.2")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.platformContentAndAvailabilityList.3")}
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.bookings")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.bookingsList.1")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.bookingsList.2")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.bookingsList.3")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.bookingsList.4")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.bookingsList.5")}
          <ul className="list-disc pl-6">
            <li>{t("TermsOfUse.bookingsList.6")}</li>
            <li>{t("TermsOfUse.bookingsList.7")}</li>
            <li>{t("TermsOfUse.bookingsList.8")}</li>
          </ul>
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.fees")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.feesList.1")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.feesList.2")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.feesList.3")}
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.thirdPartyServiceProviders")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t.rich("TermsOfUse.thirdPartyServiceProvidersList.1", {
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.thirdPartyServiceProvidersList.2")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.thirdPartyServiceProvidersList.3")}
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.assumptionOfRisks")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.assumptionOfRisksList.1")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.assumptionOfRisksList.2")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.assumptionOfRisksList.3")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.assumptionOfRisksList.4")}
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.yourRepresentationsAndWarranties")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.yourRepresentationsAndWarrantiesList.1")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.yourRepresentationsAndWarrantiesList.2")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.yourRepresentationsAndWarrantiesList.3")}
          <ul className="list-disc pl-6">
            <li>{t("TermsOfUse.yourRepresentationsAndWarrantiesList.4")}</li>
            <li>{t("TermsOfUse.yourRepresentationsAndWarrantiesList.5")}</li>
            <li> {t("TermsOfUse.yourRepresentationsAndWarrantiesList.6")}</li>
            <li>{t("TermsOfUse.yourRepresentationsAndWarrantiesList.7")}</li>
            <li>{t("TermsOfUse.yourRepresentationsAndWarrantiesList.8")}</li>
            <li>{t("TermsOfUse.yourRepresentationsAndWarrantiesList.9")}</li>
            <li>{t("TermsOfUse.yourRepresentationsAndWarrantiesList.10")}</li>
          </ul>
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.yourRepresentationsAndWarrantiesList.11")}
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.indemnity")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.indemnityList.1")}
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.disclaimers")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.disclaimersList.1")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.disclaimersList.2")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.disclaimersList.3")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.disclaimersList.4")}
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.limitationOfLiability")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.limitationOfLiabilityList.1")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.limitationOfLiabilityList.2")}
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.governingLawAndDisputeResolution")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.governingLawAndDisputeResolutionList.1")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.governingLawAndDisputeResolutionList.2")}
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.miscellaneous")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t.rich("TermsOfUse.miscellaneousList.1", {
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t.rich("TermsOfUse.miscellaneousList.2", {
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t.rich("TermsOfUse.miscellaneousList.3", {
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t.rich("TermsOfUse.miscellaneousList.4", {
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t.rich("TermsOfUse.miscellaneousList.5", {
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t.rich("TermsOfUse.miscellaneousList.6", {
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t.rich("TermsOfUse.miscellaneousList.7", {
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xl font-bold font-switzer leading-normal text-zinc-900 dark:text-white sm:text-lg">
          {t("TermsOfUse.contactInformation")}
        </h2>
        <div className="h-px bg-zinc-200" />
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          {t("TermsOfUse.contactInformationList.1")}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          <strong>{t("TermsOfUse.contactInformationList.2")}</strong>
          <button
            type="button"
            onClick={handleEmailCopy}
            aria-label="Copy customer support email"
            className="text-sleap-purple-300 hover:underline inline bg-transparent border-0 p-0 m-0 cursor-pointer focus:outline-none text-base font-normal font-switzer leading-normal"
          >
            {supportEmail}
          </button>
          <br />
          {t.rich("TermsOfUse.contactInformationList.3", {
            a: (chunks) => (
              <a
                href="/contact"
                className="text-sleap-purple-300 hover:underline"
              >
                {chunks}
              </a>
            ),
          })}
        </p>
        <p className="text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
          <strong>{tenantName}</strong>
          <br />
          {t("TermsOfUse.contactInformationList.4")}
        </p>
      </section>
    </>
  );
};
